import type { GQL } from "@binale-tech/shared";

import type { MessageId } from "@domain/intl/useTranslation";

import { AgentCancelledError, createApprove } from "./approval";
import { AgentRecordsNotLoadedError } from "./errors";
import type {
    AgentContext,
    AgentMode,
    AgentIO,
    AgentRunControl,
    AgentRunReport,
    AgentStep,
    AgentStepId,
    AgentStepResult,
    ReportLine,
} from "./types";

export const MIN_THINKING_MS = 800;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Shows a thinking line while `work` runs and keeps it at least MIN_THINKING_MS. */
export const withThinking = async <T>(
    io: Pick<AgentIO, "post" | "update">,
    textId: MessageId,
    work: () => Promise<T>,
): Promise<T> => {
    const id = io.post({ kind: "thinking", textId });
    const started = Date.now();
    try {
        return await work();
    } finally {
        const rest = MIN_THINKING_MS - (Date.now() - started);
        if (rest > 0) await sleep(rest);
        io.update(id, { kind: "info" });
    }
};

/** The number of open items of a step: the counts of its open lines, one for a line without a count. */
export const openCount = (open: ReportLine[]): number =>
    open.reduce((sum, line) => sum + (typeof line.values?.count === "number" ? line.values.count : 1), 0);

const dependencyMet = (step: AgentStep, ctx: AgentContext, report: AgentRunReport): boolean => {
    const { needs } = step;
    if (needs === undefined) return true;
    if (typeof needs === "function") return needs(ctx);
    return needs.every((id) => {
        const status = report.steps[id]?.status;
        return status === "done" || status === "nothing";
    });
};

const record = (report: AgentRunReport, id: AgentStepId, result: AgentStepResult, waitsFor?: AgentStepId[]): void => {
    report.steps[id] = {
        status: result.status,
        summary: result.summary ?? [],
        open: result.open ?? [],
        details: result.details ?? [],
        ...(result.byUser === true ? { byUser: true as const } : {}),
        ...(result.skipReason !== undefined
            ? { skipReason: result.skipReason }
            : result.byUser === true
              ? { skipReason: "byUser" as const }
              : {}),
        ...(waitsFor ? { waitsFor } : {}),
        ...(result.data?.continuesOnServer === true ? { continuesOnServer: true as const } : {}),
    };
};

/** Walks the steps in order; rights, dependencies, pause, close and errors are handled here. */
export const runAgent = async (
    steps: AgentStep[],
    ctx: AgentContext,
    runIo: Omit<AgentIO, "narrateSkipped" | "approve">,
    control: AgentRunControl,
    mode: () => AgentMode = () => "approval",
    onRecorded: (id: AgentStepId) => void = () => undefined,
    /** A rerun passes the report of its run; the steps then record into it. */
    existing?: AgentRunReport,
): Promise<AgentRunReport> => {
    const report: AgentRunReport = existing ?? { steps: {}, notRun: [], startedAt: Date.now() };
    ctx.runReport = report;
    const narratedProducts = new Set<GQL.IProductKey>();
    const narrateSkipped = (product: GQL.IProductKey) => {
        if (narratedProducts.has(product)) return;
        narratedProducts.add(product);
        runIo.post({
            kind: "skipped",
            textId: "app.agent.skippedNoRight",
            labels: { product: `app.titles.${product}` },
        });
    };
    let currentStepId: AgentStepId | undefined;
    const approve = createApprove({
        mode,
        stepId: () => currentStepId,
        isCancelled: runIo.isCancelled,
        ask: runIo.ask,
        post: runIo.post,
        update: runIo.update,
    });
    // Set when the step itself posted a line with details; the runner's grey line then leaves them out.
    let narratedDetails = false;
    const io: AgentIO = {
        ...runIo,
        post: (m) => {
            if ((m.kind === "done" || m.kind === "nothing") && (m.details ?? []).length > 0) narratedDetails = true;
            return runIo.post(m);
        },
        narrateSkipped,
        approve,
    };

    for (const [index, step] of steps.entries()) {
        await control.waitIfPaused();
        if (control.isCancelled()) {
            report.notRun = steps.slice(index).map((s) => s.id);
            break;
        }

        const missing = (step.product ?? []).filter((p) => !ctx.canWrite(p));
        if (missing.length > 0) {
            for (const product of missing) narrateSkipped(product);
            record(report, step.id, { status: "skipped", skipReason: "noRight" });
            onRecorded(step.id);
            continue;
        }

        if (!dependencyMet(step, ctx, report)) {
            const previous = Array.isArray(step.needs)
                ? step.needs.find((id) => report.steps[id]?.byUser === true)
                : undefined;
            io.post({
                kind: "skipped",
                ...(previous === undefined
                    ? {
                          textId: "app.agent.skippedDependency" as const,
                          labels: { step: `app.agent.step.${step.id}` as const },
                      }
                    : {
                          textId: "app.agent.skippedBecause" as const,
                          labels: {
                              step: `app.agent.step.${step.id}` as const,
                              previous: `app.agent.step.${previous}` as const,
                          },
                      }),
            });
            record(
                report,
                step.id,
                { status: "skipped", skipReason: "dependency" },
                Array.isArray(step.needs) ? step.needs : undefined,
            );
            onRecorded(step.id);
            continue;
        }

        let result: AgentStepResult | undefined;
        currentStepId = step.id;
        narratedDetails = false;
        report.mode = mode();
        while (result === undefined) {
            try {
                result = await step.run(ctx, io);
            } catch (error) {
                console.error(`Agent step ${step.id} failed`, error);
                const labels = { step: `app.agent.step.${step.id}` } as const;
                if (step.id === "start") {
                    result = { status: "failed" };
                    break;
                }
                const mayRetry = !(error instanceof AgentCancelledError) && !runIo.isCancelled();
                if (!mayRetry) {
                    io.post({ kind: "error", textId: "app.agent.stepFailed", labels });
                    result = { status: "failed" };
                    break;
                }
                io.post(
                    error instanceof AgentRecordsNotLoadedError
                        ? { kind: "error", textId: "app.agent.recordsNotLoaded", labels, values: { year: error.year } }
                        : { kind: "error", textId: "app.agent.stepFailedAsk", labels },
                );
                const answer = await io
                    .ask("app.agent.stepFailedRetry", [
                        { value: "retry", labelId: "app.agent.stepFailedRetry.go", primary: true },
                        { value: "continue", labelId: "app.agent.stepFailedRetry.continue" },
                    ])
                    .catch(() => "continue" as const);
                if (answer !== "retry") {
                    result = { status: "failed" };
                }
            }
        }
        record(report, step.id, result);
        onRecorded(step.id);
        // A failed start means a new run: without the overview and the mode card no later step may write.
        if (step.id === "start" && result.status === "failed") {
            if (!control.isCancelled()) io.post({ kind: "error", textId: "app.agent.startFailed" });
            report.notRun = steps.slice(index + 1).map((s) => s.id);
            break;
        }
        if (result.status === "nothing") {
            const open = result.open ?? [];
            const details = result.details ?? [];
            io.post({
                kind: "nothing",
                ...(open.length > 0
                    ? { textId: "app.agent.nothingOpen" as const, values: { count: openCount(open) } }
                    : { textId: "app.agent.nothingToDo" as const }),
                labels: { step: `app.agent.step.${step.id}` },
                ...(details.length > 0 && !narratedDetails ? { details } : {}),
            });
        }
        if (control.isCancelled() && result.data?.continuesOnServer === true) {
            io.post({ kind: "text", textId: "app.agent.continuesOnServer" });
        }
    }
    return report;
};
